import { useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmailStep } from "@/components/auth/EmailStep";
import { PasswordStep } from "@/components/auth/PasswordStep";
import { AuthMethodSelector } from "@/components/auth/AuthMethodSelector";
import { CommunicationCodeSelector } from "@/components/auth/CommunicationCodeSelector";
import { MagicLinkSent } from "@/components/auth/MagicLinkSent";
import { PasskeySetup } from "@/components/security/PasskeySetup";
import { TwoFactorStep } from "@/components/auth/TwoFactorStep";
import { BackupCodeStep } from "@/components/auth/BackupCodeStep"; // Import BackupCodeStep

import { authClient } from "@/lib/auth-client";
import { ROUTES } from "@/lib/routes";
import { authReducer, authActions, getInitialAuthState } from "@/reducers/authReducer";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";
import { toast } from "@/components/ui/sonner";
import { AuthMethod } from "@/types/state";
import { PasskeyStep as AuthPasskeyStep } from "@/components/auth/PasskeyStep";

export default function Auth() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(authReducer, getInitialAuthState());
  const { fingerprint, isLoading: isFingerprintLoading } = useDeviceFingerprint();

  // Handlers for state transitions and actions
  const handlers = {
    handleEmailSubmit: async () => {
      dispatch(authActions.setLoading(true));
      dispatch(authActions.setBackgroundChecking(true));
      dispatch(authActions.setStep('validatingEmail'));

      try {
        // Check if user exists and what auth methods are available
        // We use direct fetch to ensure the Authorization header is sent correctly
        // and to avoid any client-side stripping of headers for public endpoints.
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        
        const response = await fetch(`${supabaseUrl}/functions/v1/auth/check-user`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${anonKey}`
          },
          body: JSON.stringify({ email: state.email })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || errorData.error || "Failed to check user");
        }

        const data = await response.json();

        dispatch(authActions.setAvailableMethods(data.availableMethods));
        dispatch(authActions.setPendingUserId(data.userId));

        if (data.userExists) {
          dispatch(authActions.setMode('login'));
          dispatch(authActions.setLoginStep('method1'));
          dispatch(authActions.setStep('selectingMethod1'));
        } else {
          dispatch(authActions.setMode('signup'));
          dispatch(authActions.setSignupStep('password'));
          dispatch(authActions.setStep('promptingPassword'));
        }
      } catch (error: any) {
        console.error("Error checking user:", error);
        toast.error(error.message || "Failed to check user existence.");
        dispatch(authActions.setStep('promptingEmail'));
      } finally {
        dispatch(authActions.setLoading(false));
        dispatch(authActions.setBackgroundChecking(false));
      }
    },

    handleMethod1Selection: (method: AuthMethod) => {
      dispatch(authActions.setSelectedMethod1(method));
      switch (method) {
        case 'password':
          dispatch(authActions.setStep('promptingPassword'));
          break;
        case 'magic_link':
          handlers.handleMagicLinkSignIn();
          break;
        case 'passkey':
          handlers.handlePasskeySignIn();
          break;
        default:
          // Should not happen for method 1
          console.warn("Unexpected method selected for step 1:", method);
          break;
      }
    },

    handleMethod2Selection: (method: AuthMethod) => {
      dispatch(authActions.setSelectedMethod2(method));
      switch (method) {
        case 'totp':
          dispatch(authActions.setStep('prompting2FA'));
          break;
        case 'backup_code':
          dispatch(authActions.setStep('promptingBackupCode'));
          break;
        case 'passkey':
          handlers.handlePasskeySignIn(); // Passkey can be a second factor
          break;
        default:
          console.warn("Unexpected method selected for step 2:", method);
          break;
      }
    },

    handlePasswordSignIn: async () => {
      dispatch(authActions.setLoading(true));
      dispatch(authActions.setStep('submitting'));

      try {
        const { data, error } = await authClient.signIn.email({
          email: state.email,
          password: state.password,
        });

        if (error) {
          throw error;
        }

        // Check if 2FA is required
        if (data.twoFactorRequired) {
          dispatch(authActions.setTfaRequired(true));
          dispatch(authActions.setStep('selectingMethod2'));
          toast.info("Two-factor authentication required.");
        } else {
          toast.success("Signed in successfully!");
          navigate(ROUTES.DASHBOARD);
        }
      } catch (error: any) {
        console.error("Password sign-in error:", error);
        toast.error(error.message || "Invalid credentials.");
        dispatch(authActions.setStep('promptingPassword'));
      } finally {
        dispatch(authActions.setLoading(false));
      }
    },

    handlePasswordSignUp: async () => {
      dispatch(authActions.setLoading(true));
      dispatch(authActions.setStep('submitting'));

      try {
        const { data, error } = await authClient.signUp.email({
          email: state.email,
          password: state.password,
          communicationCode: state.communicationCode,
        });

        if (error) {
          throw error;
        }

        toast.success("Account created! Please check your email to verify.");
        dispatch(authActions.setMagicLinkSent(true));
        dispatch(authActions.setStep('magicLinkSent'));
      } catch (error: any) {
        console.error("Password sign-up error:", error);
        toast.error(error.message || "Failed to create account.");
        dispatch(authActions.setStep('promptingPassword'));
      } finally {
        dispatch(authActions.setLoading(false));
      }
    },

    handleMagicLinkSignIn: async () => {
      dispatch(authActions.setLoading(true));
      dispatch(authActions.setStep('submitting'));

      try {
        const { data, error } = await authClient.signIn.magicLink({
          email: state.email,
          callbackURL: `${window.location.origin}/verify-magic-link`,
        });

        if (error) {
          throw error;
        }

        toast.success("Magic link sent! Check your email.");
        dispatch(authActions.setMagicLinkSent(true));
        dispatch(authActions.setStep('magicLinkSent'));
      } catch (error: any) {
        console.error("Magic link sign-in error:", error);
        toast.error(error.message || "Failed to send magic link.");
        dispatch(authActions.setStep('selectingMethod1'));
      } finally {
        dispatch(authActions.setLoading(false));
      }
    },

    handleResendVerification: async () => {
      dispatch(authActions.setLoading(true));
      dispatch(authActions.setResendCooldown(60)); // 60 seconds cooldown

      try {
        const { error } = await authClient.signIn.magicLink({
          email: state.email,
          callbackURL: `${window.location.origin}/verify-magic-link`,
        });

        if (error) {
          throw error;
        }

        toast.success("Verification link re-sent!");
      } catch (error: any) {
        console.error("Resend verification error:", error);
        toast.error(error.message || "Failed to re-send verification link.");
      } finally {
        dispatch(authActions.setLoading(false));
      }
    },

    handlePasskeySignIn: async () => {
      dispatch(authActions.setLoading(true));
      dispatch(authActions.setStep('submitting'));

      try {
        const { data, error } = await authClient.signIn.passkey();

        if (error) {
          throw error;
        }

        // Check if 2FA is required after passkey authentication
        if (data.twoFactorRequired) {
          dispatch(authActions.setTfaRequired(true));
          dispatch(authActions.setStep('selectingMethod2'));
          toast.info("Two-factor authentication required.");
        } else {
          toast.success("Signed in successfully with passkey!");
          navigate(ROUTES.DASHBOARD);
        }
      } catch (error: any) {
        console.error("Passkey sign-in error:", error);
        toast.error(error.message || "Failed to sign in with passkey.");
        dispatch(authActions.setStep(state.loginStep === 'method1' ? 'selectingMethod1' : 'promptingPassword')); // Go back to method selection or password
      } finally {
        dispatch(authActions.setLoading(false));
      }
    },

    completeMagicLinkSignup: () => {
      toast.success("Passkey registered and account verified!");
      navigate(ROUTES.DASHBOARD);
    },

    handleForgotPassword: async () => {
      dispatch(authActions.setLoading(true));
      try {
        const { error } = await authClient.auth.sendPasswordResetEmail({ email: state.email });
        if (error) throw error;
        toast.success("Password reset link sent to your email.");
        dispatch(authActions.setForgotPasswordOpen(false));
      } catch (error: any) {
        console.error("Forgot password error:", error);
        toast.error(error.message || "Failed to send password reset email.");
      } finally {
        dispatch(authActions.setLoading(false));
      }
    },
  };

  // Effect to check passkey support
  useEffect(() => {
    const checkPasskeySupport = async () => {
      dispatch(authActions.setCheckingPasskeySupport(true));
      try {
        const supported = await authClient.passkey.isSupported();
        dispatch(authActions.setPasskeySupported(supported));
      } catch (error) {
        console.error("Error checking passkey support:", error);
        dispatch(authActions.setPasskeySupported(false));
      } finally {
        dispatch(authActions.setCheckingPasskeySupport(false));
      }
    };
    checkPasskeySupport();
  }, []);

  // Initial step setup
  useEffect(() => {
    if (state.step === 'idle' && !isFingerprintLoading) {
      dispatch(authActions.setStep('promptingEmail'));
    }
  }, [state.step, isFingerprintLoading]);

  // Render logic based on current step
  const renderStep = () => {
    switch (state.step) {
      case 'promptingEmail':
      case 'validatingEmail':
        return (
          <EmailStep
            state={state}
            dispatch={dispatch}
            onSubmit={handlers.handleEmailSubmit}
            onSwitchMode={() => dispatch(authActions.setMode(state.mode === 'login' ? 'signup' : 'login'))}
          />
        );
      case 'selectingMethod1':
        return (
          <AuthMethodSelector
            stepNumber={1}
            availableMethods={state.availableMethods}
            selectedMethod={state.selectedMethod1}
            onSelectMethod={handlers.handleMethod1Selection}
            userConfiguredMethods={state.availableMethods}
            loading={state.loading}
            onBack={() => dispatch(authActions.resetToEmailStep())}
            email={state.email}
          />
        );
      case 'promptingPassword':
        return (
          <PasswordStep
            state={state}
            dispatch={dispatch}
            onSubmit={state.mode === 'login' ? handlers.handlePasswordSignIn : handlers.handlePasswordSignUp}
            onBack={() => dispatch(authActions.setStep('selectingMethod1'))}
            onForgotPassword={() => handlers.handleForgotPassword()}
            onMagicLink={handlers.handleMagicLinkSignIn}
            onPasskey={handlers.handlePasskeySignIn}
            passkeyLoading={state.loading}
            isSignup={state.mode === 'signup'}
          />
        );
      case 'promptingCommunicationCode':
        return (
          <CommunicationCodeSelector
            onCodeSelected={(code) => dispatch(authActions.setCommunicationCode(code))}
          />
        );
      case 'magicLinkSent':
        return (
          <MagicLinkSent
            state={state}
            dispatch={dispatch}
            onResend={handlers.handleResendVerification}
            onBack={() => {
              dispatch(authActions.setMagicLinkSent(false));
              dispatch(authActions.setStep('selectingMethod1'));
            }}
          />
        );
      case 'settingUpPasskey':
        return (
          <PasskeySetup
            onComplete={handlers.completeMagicLinkSignup}
          />
        );
      case 'selectingMethod2':
        return (
          <AuthMethodSelector
            stepNumber={2}
            availableMethods={state.availableMethods}
            selectedMethod={state.selectedMethod2}
            onSelectMethod={handlers.handleMethod2Selection}
            excludeMethod={state.selectedMethod1 || undefined}
            userConfiguredMethods={state.availableMethods}
            loading={state.loading}
            onBack={() => dispatch(authActions.setStep('selectingMethod1'))}
            email={state.email}
          />
        );
      case 'prompting2FA':
        return (
          <TwoFactorStep
            state={state}
            dispatch={dispatch}
            onSuccess={() => navigate(ROUTES.DASHBOARD)}
            onBack={() => dispatch(authActions.setStep('selectingMethod2'))}
          />
        );
      case 'promptingBackupCode':
        return (
          <BackupCodeStep
            onSuccess={() => navigate(ROUTES.DASHBOARD)}
            onBack={() => dispatch(authActions.setStep('selectingMethod2'))}
          />
        );
      case 'promptingPasskey':
        return (
          <AuthPasskeyStep
            stepNumber={2}
            onSuccess={() => navigate(ROUTES.DASHBOARD)}
            onBack={() => dispatch(authActions.setStep('selectingMethod2'))}
          />
        );
      case 'submitting':
        return (
          <div className="flex flex-col items-center justify-center space-y-4 py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">
              {state.mode === 'login' ? 'Signing in...' : 'Creating account...'}
            </p>
          </div>
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center space-y-4 py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading authentication...</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="absolute left-4 top-4"
            disabled={state.loading}
          >
            {/* <ArrowLeft className="h-4 w-4" /> */}
          </Button>
          <CardTitle className="text-2xl font-bold">
            {state.mode === 'login' ? 'Sign In' : 'Sign Up'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderStep()}
        </CardContent>
      </Card>
    </div>
  );
}
